import { useFormStore, useStoreState } from '@ariakit/react'
import { isEqual, omit } from 'lodash-es'
import { ReactNode, useEffect, useMemo, useRef } from 'react'

import { useZodFormValidate } from '../../app/_components/form/useZodFormValidate'
import { MaybePromise } from '../../types/utility'

import { useEmbroidery } from './EmbroideryProvider'
import { EmbroideryFormKind, EmbroideryFormSubmissions, EmbroideryFormValues } from './types'

import type { ZodType } from 'zod'

type UseEmbroideryFormOpts<T extends keyof EmbroideryFormValues> = {
  /* The validation schema that should be applied for the current form. */
  validationSchema?: ZodType
  /* Augment the initial form values. Provided with the default sticky/initial form values for the form. */
  createInitialValues?: (initialValues: EmbroideryFormValues[T]) => EmbroideryFormValues[T]
  /* Create the embroidery options that should be displayed for the given embroidery form. Throw on failure. */
  createEmbroideryOptions: (
    values: EmbroideryFormValues[T]
  ) => MaybePromise<EmbroideryFormSubmissions[T]>
  /* Render the preview item that should be displayed for the given embroidery form. */
  previewItem?: (values: EmbroideryFormValues[T]) => ReactNode
  /* Define which form kind should be set after submission. Defaults to null. */
  finalFormKind?: EmbroideryFormKind
  /* Callback function that runs after form submission. */
  onSubmitCompleted?: (values: EmbroideryFormValues[T]) => void
  /* Callback function that runs when createEmbroideryOptions throws. */
  onSubmitError?: (error: unknown) => void
}

type ResetFormOptions = {
  skipCreateInitialValues?: boolean
  clearSticky?: boolean
}

/** Initialize the form state for a given embroidery form. */
export function useEmbroideryForm<T extends keyof EmbroideryFormValues>(
  kind: T,
  {
    validationSchema,
    createInitialValues,
    createEmbroideryOptions,
    previewItem,
    finalFormKind,
    onSubmitCompleted,
    onSubmitError,
  }: UseEmbroideryFormOpts<T>
) {
  const {
    stickyFormValuesByKind,
    setStickyFormValuesByKind,
    setSelectedFormKind,
    setPreviewItemsByKind,
    setEmbroiderySelections,
    prefilledFormValuesByKind,
    clearedFormValuesByKind,
  } = useEmbroidery()

  // Derive the initial form values based on the sticky form values for the given embroidery kind.
  // If `createInitialValues` is provided, use it to augment the initial form values applied to the form.
  const initialFormValues = useMemo(() => {
    const initialFormStateValues = stickyFormValuesByKind[kind] ?? prefilledFormValuesByKind[kind]!
    return createInitialValues
      ? createInitialValues(initialFormStateValues)
      : initialFormStateValues
  }, [stickyFormValuesByKind, prefilledFormValuesByKind, kind, createInitialValues])

  const isSubmitInFlightRef = useRef(false)

  const form = useFormStore<EmbroideryFormValues[T]>({ defaultValues: initialFormValues })
  useStoreState(form)
  const storeState = form.getState()
  const currentValues = storeState.values
  const submitFailed = storeState.submitFailed
  const errors = storeState.errors

  const lastSyncedValuesRef = useRef(form.getState().values)
  const isDirty = !isEqual(currentValues, lastSyncedValuesRef.current)

  useZodFormValidate(form, validationSchema)

  form.useSubmit(async state => {
    if (isSubmitInFlightRef.current) return
    isSubmitInFlightRef.current = true
    try {
      const selectedEmbroideryOptions = await createEmbroideryOptions(state.values)
      setStickyFormValuesByKind(prev => ({ ...prev, [kind]: state.values }))
      setEmbroiderySelections(prev => ({ ...prev, [kind]: selectedEmbroideryOptions }))
      setSelectedFormKind(finalFormKind ?? null)
      if (onSubmitCompleted) onSubmitCompleted(state.values)
    } catch (error) {
      if (onSubmitError) onSubmitError(error)
    } finally {
      isSubmitInFlightRef.current = false
    }
  })

  // Set the preview item for the given embroidery kind
  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (previewItem) {
      setPreviewItemsByKind(prev => ({
        ...prev,
        [kind]: [previewItem(currentValues)],
      }))
    }
    // We only want to update the preview item when form values change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValues, kind])

  // Sync the form with new initial values only if it hasn't been touched.
  // This lets async prefilled values (such as saved preferences) populate the form
  // without overwriting sticky state, user edits, or retriggering with identical values.
  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (stickyFormValuesByKind[kind] || isDirty || isEqual(currentValues, initialFormValues)) {
      return
    }
    form.setValues(initialFormValues)
    lastSyncedValuesRef.current = initialFormValues
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormValues, kind, stickyFormValuesByKind])

  // Focus on first invalid field on submit
  useEffect(() => {
    if (submitFailed > 0 && Object.values(errors).some(Boolean)) {
      const firstErrorField = Object.keys(errors).find(k => errors[k as keyof typeof errors])
      const field = firstErrorField
        ? (document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement | null)
        : null
      if (field?.scrollIntoView) {
        field.focus()
        field.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }
    }
  }, [submitFailed, errors])

  const resetForm = ({ skipCreateInitialValues, clearSticky }: ResetFormOptions = {}) => {
    const defaultFormValues =
      !skipCreateInitialValues && createInitialValues
        ? createInitialValues(clearedFormValuesByKind[kind]!)
        : clearedFormValuesByKind[kind]!
    form.setValues(defaultFormValues)
    lastSyncedValuesRef.current = defaultFormValues
    Object.keys(form.getState().errors).forEach(k => form.setError(k, ''))
    form.setTouched({})
    setStickyFormValuesByKind(prev => ({
      ...prev,
      [kind]: clearSticky ? undefined : defaultFormValues,
    }))
    setEmbroiderySelections(prev => omit(prev, kind))
  }

  return { form, resetForm, isDirty }
}
